import React from "react";
import { Link } from "react-router-dom";
import { Shield, Sparkles, Briefcase, BookOpen, ArrowRight } from "lucide-react";
import { HologramPortrait } from "@/components/fx/HologramPortrait";
import { CapabilityMap } from "@/components/about/CapabilityMap";
import { UsageHandbook } from "@/components/about/UsageHandbook";
import { TID } from "@/lib/testIds";

export default function About() {
    return (
        <div className="max-w-5xl mx-auto space-y-6">
            {/* Identity */}
            <div className="panel-elevated p-10 relative overflow-hidden">
                <img
                    src="/assets/sentinel/hud-landscape.webp"
                    alt=""
                    aria-hidden="true"
                    data-testid="about-hero-art"
                    className="absolute inset-0 w-full h-full object-cover object-[62%_center] opacity-[0.22] theme-photo pointer-events-none"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#07070A]/95 via-[#07070A]/75 to-[#07070A]/40 pointer-events-none" />
                <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none" />
                <div className="absolute -left-8 -bottom-12 w-72 h-72 rounded-full bg-cyan-600/15 blur-3xl pointer-events-none" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                    {[0, 1, 2].map((i) => (
                        <span
                            key={i}
                            className="absolute rounded-full border border-cyan-400/15"
                            style={{
                                width: `${320 + i * 160}px`,
                                height: `${320 + i * 160}px`,
                                left: `${-(320 + i * 160) / 2}px`,
                                top: `${-(320 + i * 160) / 2}px`,
                                animation: `vfxPulse ${6 + i * 1.5}s ease-in-out ${i * 0.5}s infinite`,
                            }}
                        />
                    ))}
                </div>
                <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />

                <div className="flex items-center gap-4 mb-6 relative">
                    <div className="w-14 h-14 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_24px_rgb(var(--accent-1-rgb) / 0.45)]">
                        <Shield size={26} className="text-slate-950" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="label-eyebrow">v1.0 · Enterprise Offline Edition</div>
                        <h1 className="font-display text-3xl text-white">UTSAVA <span className="text-cyan-400">Genesis</span></h1>
                        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-500/70 mt-1">um-alphabydesign</div>
                    </div>
                </div>

                <div className="space-y-1 text-slate-300 relative">
                    <p className="font-display text-lg">Unified Trusted Security Authorization &amp; Validation Architecture</p>
                    <p className="text-sm text-slate-400">Enterprise Offline SAP Security Engineering Workspace — role engineering, SoD governance, and a conversational AI agent, all reasoning entirely on artifacts you control.</p>
                </div>

                <div className="divider-glow my-8 relative" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                    <div>
                        <div className="label-eyebrow mb-2">Powered by</div>
                        <div className="text-white text-lg font-display flex items-center gap-2">
                            <Sparkles size={16} className="text-cyan-400" /> ASSEP
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Autonomous SAP Security Engineering Platform</p>
                    </div>
                    <div>
                        <div className="label-eyebrow mb-2">Innovation Lead</div>
                        <div className="text-white text-lg font-display">Utsav Mukherjee</div>
                        <p className="text-xs text-slate-400 mt-2">Advisory Solution Architect</p>
                    </div>
                </div>

                <p className="text-sm text-slate-400 leading-relaxed mt-8 relative">
                    UTSAVA Genesis is designed for SAP Security Architects, Consultants, GRC professionals, Auditors and AMS teams
                    who need to inspect, reason about, and recommend remediations for SAP engineering artifacts — without requiring
                    any live SAP connectivity. <span className="text-cyan-300">Sentinel Forge</span> mass-engineers PFCG roles from
                    T-codes, real Fiori Business Catalogs, or 30+ data-driven templates — every role is instantly checked against a
                    258-risk / 26-critical-activity SoD ruleset imported from a genuine SAP GRC Access Control export, down to
                    field/ACTVT-level verification. Legacy T-code-only roles can be one-click <span className="text-cyan-300">migrated
                    to Fiori Catalogs</span> via a set-cover conversion helper, and every role can be exported as a byte-accurate
                    <span className="text-cyan-300"> .SAP</span> reference file (now including the Fiori launchpad menu hierarchy).
                    <span className="text-cyan-300"> Utsav</span>, the in-app AI agent, now rendered as your own cinematic 3D
                    hologram, talks, listens, and remembers across sessions — able to look up any T-code, run a live SoD simulation,
                    or draft a BPD/PDD on request. Every recommendation is explainable; every change requires human approval; every
                    action is audited.
                </p>

                <p className="text-sm text-slate-400 leading-relaxed mt-4 relative">
                    The newest addition is the <span className="text-cyan-300">SAP Diagram Studio</span> — a deterministic,
                    text-driven diagram engine (no raster image models, so every T-code and authorization object stays crisp and
                    searchable). Pick from the SAP template library — <span className="font-mono text-slate-300">Three-Tier Role
                    Model</span>, <span className="font-mono text-slate-300">SoD Conflict Matrix</span>,
                    <span className="font-mono text-slate-300"> Authorization Concept</span>,
                    <span className="font-mono text-slate-300"> Procure-to-Pay</span> and
                    <span className="font-mono text-slate-300"> Order-to-Cash</span> process flows — and the engine renders your
                    real role data, colour-coding SoD-conflicted transactions. Every diagram exports as vector
                    <span className="text-cyan-300"> SVG</span> or high-resolution <span className="text-cyan-300">PNG</span>, is
                    versioned into a per-role <span className="text-cyan-300">diagram history</span> you can restore at any time,
                    and can be <span className="text-cyan-300">iteratively refined in natural language</span> by the UTSAVA Reasoner
                    ("collapse the org levels", "add the approval control"). Cross-role simulations can be visualised as a
                    <span className="text-cyan-300"> toxic-combination matrix</span>, and generated diagrams are embedded straight
                    into the BPD / PDD <span className="font-mono text-slate-300">.docx</span> deliverables.
                </p>

                <p className="text-sm text-slate-400 leading-relaxed mt-4 relative">
                    The engineering surface now extends into custom code. The
                    <span className="text-cyan-300"> ABAP Code Review &amp; RICEFW Security Clearance</span> engine performs
                    authorization-centric static analysis on pasted or uploaded ABAP — missing
                    <span className="font-mono text-slate-300"> AUTHORITY-CHECK</span>, unchecked
                    <span className="font-mono text-slate-300"> sy-subrc</span>, dangerous
                    <span className="font-mono text-slate-300"> SUBMIT</span>/dynamic calls, hardcoded users and client-open
                    selects — rates each finding, offers a corrected code pattern, ingests your
                    <span className="text-cyan-300"> ATC error exports</span> (<span className="font-mono text-slate-300">.xlsx</span>)
                    and issues a downloadable <span className="text-cyan-300">RICEFW clearance report</span> with a pass /
                    conditional / fail verdict.
                </p>

                <p className="text-sm text-slate-400 leading-relaxed mt-4 relative">
                    Three governance upgrades close the audit loop. The
                    <span className="text-cyan-300"> APO / CRM / SRM enrichment overlay</span> revives the 74 SoD risks and 4
                    critical activities that the raw GRC export left structurally dormant — and because it is a separately
                    tagged provenance layer, every rule and Function in the app is badged
                    <span className="font-mono text-slate-300"> GRC EXPORT</span> or
                    <span className="font-mono text-slate-300"> ENRICHED</span> (with a confidence rating and its SAP basis),
                    so an auditor always knows where a rule came from. Every recommendation approval or rejection now
                    automatically attaches its own <span className="text-cyan-300">Change Impact Brief</span> — the blast radius
                    through the Knowledge Graph, frozen at decision time and downloadable as a PDF approval record. And the
                    <span className="text-cyan-300"> Weekly Posture Digest</span> bundles new findings, decisions (and which of
                    them carry impact evidence), the most toxic roles, ruleset coverage and RICEFW verdicts into one scheduled,
                    exportable report — generated offline, on your own clock, with week-over-week deltas.
                </p>

                <p className="text-sm text-slate-400 leading-relaxed mt-4 relative">
                    <span className="text-cyan-300">Utsav</span>, the in-app agent, is now a full working surface: attach ABAP
                    sources, ATC / SU24 spreadsheets, PDFs, Word documents or screenshots straight into the conversation and he
                    reads, parses and reasons over them — then take the work back out as a
                    <span className="font-mono text-slate-300"> PDF</span> or
                    <span className="font-mono text-slate-300"> Markdown</span> transcript, per-message
                    <span className="font-mono text-slate-300"> .abap</span> code files, or any table as
                    <span className="font-mono text-slate-300"> .xlsx</span>.
                </p>

                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-center relative">
                    {[
                        { k: "Role Engineering", v: "Sentinel Forge · T-Code + Fiori Catalog" },
                        { k: "Code Review", v: "ABAP Static Analysis · RICEFW Clearance" },
                        { k: "ATC Audit", v: "Ingest ATC .xlsx · Corrected Patterns" },
                        { k: "Fiori Migration", v: "One-click Catalog Conversion Helper" },
                        { k: "AI Agent", v: "Utsav · Chat, Voice, Memory, Files" },
                        { k: "SoD Ruleset", v: "258 Risks · 26 Critical Activities" },
                        { k: "Rule Provenance", v: "GRC Export vs Enriched · Badged" },
                        { k: "Enrichment", v: "APO/CRM/SRM · 74 Risks Revived" },
                        { k: "Change Evidence", v: "Impact Brief on Every Approval" },
                        { k: "Posture Digest", v: "Weekly · Scheduled · PDF" },
                        { k: "Reasoner", v: "UTSAVA Reasoner · Deep / Fast" },
                        { k: "Governance", v: "Compliance Map · Mitigation Library" },
                        { k: "Knowledge", v: "Cytoscape AKG · 3D Depth Backdrop" },
                        { k: "Engines", v: "Authorization · SU24 · OData · ABAP" },
                        { k: "Instant Analysis", v: "Paste or Upload · Zero Setup" },
                        { k: "Dynamic RBAC", v: "Admin-Defined Custom Roles" },
                        { k: "Diagram Studio", v: "5 SAP Templates · SVG + PNG" },
                        { k: "Cross-Role SoD", v: "Toxic-Combination Matrix Visual" },
                        { k: "Exports", v: "PDF · DOCX · XLSX · PPTX · .SAP · MD" },
                        { k: "Mode", v: "Offline · Human-Approved · Audited" },
                    ].map((b) => (
                        <div key={b.k} className="panel px-3 py-3">
                            <div className="label-eyebrow">{b.k}</div>
                            <div className="text-xs text-slate-200 font-mono mt-1">{b.v}</div>
                        </div>
                    ))}
                </div>

                <Link
                    to="/docs"
                    data-testid="about-guidebook-link"
                    className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-cyan-400 hover:text-cyan-300 transition-colors relative"
                >
                    <BookOpen size={13} /> Open the full Guide Book &amp; Playbook <ArrowRight size={12} />
                </Link>
            </div>

            <CapabilityMap />
            <UsageHandbook />

            {/* Creator Space */}
            <div className="panel-elevated relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
                <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-0">
                    {/* Photo — cinematic 3D AI Agent hologram */}
                    <div className="relative p-6 md:p-8 flex items-center justify-center md:border-r border-white/5">
                        <HologramPortrait
                            testId={TID.aboutCreatorHologram}
                            className="w-[250px] h-[250px]"
                            label="Creator · Founder"
                        />
                    </div>

                    {/* Bio */}
                    <div className="p-6 md:p-10 relative">
                        <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                            <Sparkles size={11} /> Creator Space
                        </div>
                        <h2 className="font-display text-4xl text-white tracking-tight leading-tight">
                            Utsav <span className="text-cyan-300">Mukherjee</span>
                        </h2>
                        <div className="mt-2 text-sm text-slate-400 font-mono uppercase tracking-wider flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="flex items-center gap-1"><Briefcase size={11} /> Advisory Solution Architect</span>
                            <span className="text-slate-700">·</span>
                            <span>SAP Security · Authorization · GRC</span>
                        </div>

                        <div className="divider-glow my-6" />

                        <p className="text-sm text-slate-300 leading-relaxed">
                            Utsav is an independent SAP Security &amp; Authorization architect and the
                            sole creator of <span className="text-cyan-300">UTSAVA Genesis</span> — a
                            platform born out of years of hands-on engineering on complex S/4HANA
                            implementations, AMS programs, and audit-grade authorization reviews.
                        </p>
                        <p className="text-sm text-slate-400 leading-relaxed mt-3">
                            The mission: bring autonomous, explainable engineering to every SAP
                            security professional — without ever requiring a live system connection.
                            UTSAVA stands for <span className="font-mono text-cyan-300">Unified Trusted
                            Security Authorization &amp; Validation Architecture</span>, and is offered
                            as an individual proprietary product.
                        </p>

                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {[
                                { k: "Domain", v: "SAP Security" },
                                { k: "Specialty", v: "Authorization" },
                                { k: "Method", v: "Engineering-first" },
                            ].map((b) => (
                                <div key={b.k} className="panel px-3 py-2">
                                    <div className="label-eyebrow">{b.k}</div>
                                    <div className="text-xs text-slate-200 mt-1 font-mono">{b.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-400 text-center pb-2">
                © 2026 UTSAVA Genesis - um-alphabydesign · ASSEP — Individual Proprietorship · All Rights Reserved
            </div>
        </div>
    );
}
